import React, { useEffect, useRef, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'
import { useEventRegRefreshContext } from '../reducers/event-reg-refresh-reducers'
import { IEpiProps, IEventRegistrationRefreshModel } from '../models/event-reg-refresh-models'
import { formatLocationDisplay } from '../helpers/helpers'
import { LoadingSpinner } from './loading-spinner'

interface LocationFilterProps {
  cityStateInput: string
  postalCodeInput: string
  rangeInput: string
  onCityStateChange: (value: string) => void
  onPostalCodeChange: (value: string) => void
  onRangeChange: (value: string) => void
  isOpen: boolean
  onToggle: () => void
  onApply: (cityState: string, postalCode: string) => void
  isApplying?: boolean
}

interface IContextProps {
  epiProps: IEpiProps
  eventRegistrationRefreshModel: IEventRegistrationRefreshModel
}

const MIN_SUGGESTION_QUERY_LENGTH = 2

export const LocationFilter: React.FC<LocationFilterProps> = ({
  cityStateInput,
  postalCodeInput,
  rangeInput,
  onCityStateChange,
  onPostalCodeChange,
  onRangeChange,
  isOpen,
  onToggle,
  onApply,
  isApplying = false
}) => {
  const { epiProps, eventRegistrationRefreshModel }: IContextProps = useEventRegRefreshContext()!
  const filterRef = useRef<HTMLDivElement>(null)

  const [citySuggestions, setCitySuggestions] = useState<string[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1)
  const [debouncedCityStateInput] = useDebounceValue(cityStateInput, 400)
  const suggestionListId = 'location-city-suggestions'

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node) && isOpen) {
        onToggle()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen, onToggle])

  // Debounced City/State typeahead from upcoming events.
  useEffect(() => {
    const query = debouncedCityStateInput.trim()

    if (query.length < MIN_SUGGESTION_QUERY_LENGTH) {
      setCitySuggestions([])
      return
    }

    let cancelled = false

    const fetchSuggestions = async () => {
      try {
        const response = await fetch(`/internalapi/EventRegistrationRefreshApi/LocationSuggestions?query=${encodeURIComponent(query)}`, {
          headers: { 'Accept': 'application/json' }
        })

        if (!response.ok || cancelled) return

        const data = await response.json()
        if (!cancelled) {
          setCitySuggestions(data?.suggestions ?? [])
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching location suggestions:', err)
          setCitySuggestions([])
        }
      }
    }

    fetchSuggestions()

    return () => { cancelled = true }
  }, [debouncedCityStateInput])

  useEffect(() => {
    setActiveSuggestionIndex(-1)
  }, [citySuggestions])

  useEffect(() => {
    if (activeSuggestionIndex < 0) {
      return
    }

    document.getElementById(`${suggestionListId}-${activeSuggestionIndex}`)?.scrollIntoView({ block: 'nearest' })
  }, [activeSuggestionIndex, suggestionListId])

  const isSuggestionListOpen = showSuggestions && citySuggestions.length > 0

  const handleSelectSuggestion = (suggestion: string) => {
    onCityStateChange(suggestion)
    setCitySuggestions([])
    setShowSuggestions(false)
    setActiveSuggestionIndex(-1)
  }

  const handleApply = () => {
    setShowSuggestions(false)
    if (isOpen) {
      onToggle()
    }
    onApply(cityStateInput, postalCodeInput)
  }

  const handleCityKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      if (!isSuggestionListOpen) {
        return
      }

      event.preventDefault()
      setActiveSuggestionIndex((current) =>
        current < citySuggestions.length - 1 ? current + 1 : 0)
      return
    }

    if (event.key === 'ArrowUp') {
      if (!isSuggestionListOpen) {
        return
      }

      event.preventDefault()
      setActiveSuggestionIndex((current) =>
        current <= 0 ? citySuggestions.length - 1 : current - 1)
      return
    }

    if (event.key === 'Escape') {
      if (!isSuggestionListOpen) {
        return
      }

      event.preventDefault()
      setShowSuggestions(false)
      setActiveSuggestionIndex(-1)
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()

      if (isSuggestionListOpen) {
        const selectedIndex = activeSuggestionIndex >= 0 ? activeSuggestionIndex : 0
        handleSelectSuggestion(citySuggestions[selectedIndex])
        return
      }

      handleApply()
    }
  }

  const locationDisplay = formatLocationDisplay(cityStateInput, postalCodeInput)
  const getAccordionHeading = () => {
    if (isOpen || !locationDisplay) {
      return epiProps.locationFilterHeader
    }
    return locationDisplay
  }

  const unit = eventRegistrationRefreshModel.distanceUnitLabel;

  return (
    <div className="app-dropdown-container filters-location" ref={filterRef}>
      <button className={`app-dropdown-toggle`} aria-expanded={isOpen} onClick={onToggle}>
        <span className="app-dropdown-toggle-text" title={locationDisplay || undefined}>{getAccordionHeading()}</span>
        <svg className="svg-icon" aria-hidden="true" focusable="false">
          <use xmlnsXlink="ttp://www.w3.org/2000/xlink" xlinkHref="#icon-chevron-down" />
        </svg>
      </button>

      {isOpen && (
        <div className="app-dropdown-panel">
          <div className="filters-location-form">
            <label>{epiProps.cityStateInputLabel || epiProps.locationInputLabel}</label>
            <div className="filters-location-form-fields filters-location-form-fields-autosuggest">
              <input
                type="text"
                value={cityStateInput}
                onChange={(e) => {
                  onCityStateChange(e.target.value)
                  setShowSuggestions(true)
                }}
                onFocus={() => setShowSuggestions(true)}
                onKeyDown={handleCityKeyDown}
                placeholder={epiProps.cityStateInputPlaceholder || epiProps.locationInputPlaceholder}
                autoComplete="off"
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={isSuggestionListOpen}
                aria-controls={suggestionListId}
                aria-autocomplete="list"
                aria-activedescendant={
                  isSuggestionListOpen && activeSuggestionIndex >= 0
                    ? `${suggestionListId}-${activeSuggestionIndex}`
                    : undefined
                }
              />
              {isSuggestionListOpen && (
                <ul id={suggestionListId} className="filters-location-suggestions" role="listbox">
                  {citySuggestions.map((suggestion, index) => (
                    <li
                      key={suggestion}
                      id={`${suggestionListId}-${index}`}
                      role="option"
                      aria-selected={index === activeSuggestionIndex}
                    >
                      <button
                        type="button"
                        tabIndex={-1}
                        className={index === activeSuggestionIndex ? 'is-active' : undefined}
                        onMouseEnter={() => setActiveSuggestionIndex(index)}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => handleSelectSuggestion(suggestion)}
                      >
                        {suggestion}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <label>{epiProps.postalCodeInputLabel || 'Zip/Postal Code'}</label>
            <div className="filters-location-form-fields">
              <input
                type="text"
                value={postalCodeInput}
                onChange={(e) => onPostalCodeChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApply() } }}
                placeholder={epiProps.postalCodeInputPlaceholder || 'Zip/Postal Code'}
                autoComplete="off"
              />
            </div>
          </div>
          <div>
            <p><strong>{epiProps.distanceFromLocationText}</strong></p>
            <div className="filters-options-row">
              <label className="filters-options-row-item">
                <input
                  type="radio"
                  value="50"
                  checked={rangeInput === "50"}
                  onChange={(e) => onRangeChange(e.target.value)}
                />
                50 {unit}
              </label>
              <label className="filters-options-row-item">
                <input
                  type="radio"
                  value="100"
                  checked={rangeInput === "100"}
                  onChange={(e) => onRangeChange(e.target.value)}
                />
                100 {unit}
              </label>
              <label className="filters-options-row-item">
                <input
                  type="radio"
                  value="1000"
                  checked={rangeInput === "1000"}
                  onChange={(e) => onRangeChange(e.target.value)}
                />
                250+ {unit}
              </label>
            </div>
          </div>
          <div className="filters-location-actions">
            <button
              type="button"
              className="btn-primary"
              onClick={handleApply}
              disabled={isApplying}
            >
              {epiProps.applyFiltersButtonText} {isApplying && <LoadingSpinner />}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
